/**
 * /shop/ filter-rail price slider.
 *
 * Drives the dual-range price filter:
 *   - Min value gap between thumbs = `step` ($1). The user can set
 *     a range as narrow as e.g. $2000 / $2001 — at that point the
 *     thumbs are geometrically on top of each other, but the visual
 *     no-overlap pass below renders them edge-to-edge instead.
 *   - Visual no-overlap rule: when the geometric pixel distance
 *     between thumbs is less than THUMB_PX, translateX each input
 *     by half the deficit so the visible thumbs render touching
 *     (0px apart) instead of stacked. The entire input is shifted
 *     (not just the ::thumb pseudo) so the hit-test stays
 *     consistent with the visual — clicking the visible thumb
 *     still grabs the right one.
 *   - On `change` (release / blur), dispatches `shop:filter-change`
 *     on the form so the shop-ajax driver re-fetches.
 *   - Listens for `shop:state-restored` (fired by the driver on
 *     popstate) and repaints to match the new input values.
 *
 * Markup contract (page-shop.php):
 *   <div class="tx-shop__rng-wrap"
 *        data-shop-price-range
 *        data-price-floor="N"
 *        data-price-ceiling="N">
 *     <div class="tx-shop__rng-track"></div>
 *     <div class="tx-shop__rng-fill" style="left:%; right:%"></div>
 *     <input type="range" name="price_min" data-shop-price-min ...>
 *     <input type="range" name="price_max" data-shop-price-max ...>
 *   </div>
 */
const initWrap = (wrap) => {
    const minInput = wrap.querySelector('[data-shop-price-min]');
    const maxInput = wrap.querySelector('[data-shop-price-max]');
    const fill     = wrap.querySelector('.tx-shop__rng-fill');
    // Labels live in the same <details> group as the slider — scope
    // the lookup there so multiple rails (shop + product-grid blocks)
    // each drive their own meta line.
    const meta     = wrap.closest('details') || document;
    const minLabel = meta.querySelector('[data-shop-price-min-label]');
    const maxLabel = meta.querySelector('[data-shop-price-max-label]');
    const form     = wrap.closest('form');
    if (!minInput || !maxInput || !fill) return;

    const floor   = parseFloat(wrap.dataset.priceFloor);
    const ceiling = parseFloat(wrap.dataset.priceCeiling);
    const step    = parseFloat(minInput.step) || 1;
    const span    = Math.max(1, ceiling - floor);

    // Thumb width in px — must match the CSS thumb size in shop.scss
    // (.tx-shop__rng-wrap input[type=range]::-webkit-slider-thumb width).
    // Used only by the visual no-overlap pass; it does NOT bound the
    // value gap (which is allowed down to `step`).
    const THUMB_PX = 16;

    const fmt = (n) => Math.round(n).toLocaleString();

    // Raise the thumb closest to the cursor on pointer-down so the
    // user can always grab whichever one they were aiming at. Stays
    // important even with the visual no-overlap pass: at minimum
    // separation the two thumbs are edge-to-edge, and the click
    // can land on either of them.
    const liftClosestThumb = (e) => {
        const rect = wrap.getBoundingClientRect();
        if (rect.width <= 0) return;
        const ratio     = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const cursorVal = floor + ratio * span;
        const lo        = parseFloat(minInput.value);
        const hi        = parseFloat(maxInput.value);
        const pick      = Math.abs(cursorVal - lo) <= Math.abs(cursorVal - hi) ? 'min' : 'max';

        minInput.style.zIndex = pick === 'min' ? '3' : '2';
        maxInput.style.zIndex = pick === 'max' ? '3' : '2';
    };
    wrap.addEventListener('pointerdown', liftClosestThumb);

    /**
     * Decouple visual position from value position when the value
     * gap would cause the thumbs to overlap.
     *
     * Geometric pixel distance between thumbs = (gap/span) * width.
     * Whenever that's less than THUMB_PX, write a CSS var
     * --rng-touch-shift = (THUMB_PX - geometric_px) / 2 on the wrap,
     * and toggle the `.is-touching` class. The SCSS rule then
     * translates the min input left and the max input right by
     * that amount, so the thumbs visually touch at 0px instead of
     * piling on top of each other. The fill bar gets the same
     * widening via negative margins so it stretches across the
     * visual gap.
     *
     * Boundary edge case: if either thumb is near floor / ceiling,
     * the shift may push it slightly off the track. Acceptable —
     * tightening the range against the absolute boundary is a corner
     * case, and the catalog's price span is wide enough that real
     * filtering happens in the interior.
     */
    const adjustVisualOffset = () => {
        const rect = wrap.getBoundingClientRect();
        if (rect.width <= 0) return;
        const lo = parseFloat(minInput.value);
        const hi = parseFloat(maxInput.value);
        const gapPx = ((hi - lo) / span) * rect.width;
        const overlap = THUMB_PX - gapPx;
        if (overlap > 0) {
            wrap.style.setProperty('--rng-touch-shift', `${overlap / 2}px`);
            wrap.classList.add('is-touching');
        } else {
            wrap.classList.remove('is-touching');
            wrap.style.removeProperty('--rng-touch-shift');
        }
    };

    const paint = () => {
        const lo = parseFloat(minInput.value);
        const hi = parseFloat(maxInput.value);
        fill.style.left  = (((lo - floor) / span) * 100) + '%';
        fill.style.right = (((ceiling - hi) / span) * 100) + '%';
        if (minLabel) minLabel.textContent = fmt(lo);
        if (maxLabel) maxLabel.textContent = fmt(hi);
        adjustVisualOffset();
    };

    // rAF-batch the visual paint so a 120Hz mouse can't trigger
    // 120 layout-thrashing read/write cycles per second. The push-
    // other-thumb constraint logic in the input handlers still
    // mutates input values synchronously (so drag feels live);
    // only the visual paint is deferred to the next frame.
    let paintPending = false;
    const schedulePaint = () => {
        if (paintPending) return;
        paintPending = true;
        requestAnimationFrame(() => {
            paintPending = false;
            paint();
        });
    };

    // Value-gap constraint: thumbs can sit exactly `step` apart in
    // values ($1). When dragged into each other, push the other
    // along to maintain step; clamp if the other is pinned at its
    // end of the track.
    minInput.addEventListener('input', () => {
        const lo = parseFloat(minInput.value);
        const hi = parseFloat(maxInput.value);
        if (lo > hi - step) {
            const pushedHi = lo + step;
            if (pushedHi <= ceiling) {
                maxInput.value = String(pushedHi);
            } else {
                minInput.value = String(ceiling - step);
            }
        }
        paint();
    });
    maxInput.addEventListener('input', () => {
        const lo = parseFloat(minInput.value);
        const hi = parseFloat(maxInput.value);
        if (hi < lo + step) {
            const pushedLo = hi - step;
            if (pushedLo >= floor) {
                minInput.value = String(pushedLo);
            } else {
                maxInput.value = String(floor + step);
            }
        }
        paint();
    });

    // Resize affects the value-gap → pixel-gap mapping, so re-run
    // the visual offset pass.
    window.addEventListener('resize', paint);

    // The slider lives inside a <details> group. If that group is
    // closed at init (or closed-then-reopened), the wrap has 0px
    // width and adjustVisualOffset() early-exits without writing
    // a shift — so the thumbs would render with stale geometry on
    // first reopen. Repaint on every toggle to recompute against
    // the now-visible width.
    wrap.closest('details')?.addEventListener('toggle', (e) => {
        if (e.currentTarget.open) paint();
    });

    // On release (change), tell shop-ajax to re-fetch.
    const onCommit = () => {
        if (!form) return;
        form.dispatchEvent(new CustomEvent('shop:filter-change', { bubbles: true }));
    };
    minInput.addEventListener('change', onCommit);
    maxInput.addEventListener('change', onCommit);

    // shop-ajax fires this after a popstate has rewritten the URL +
    // synced our input values. Repaint to match.
    document.addEventListener('shop:state-restored', paint);

    // Initial paint — keeps the fill bar honest if the server-rendered
    // inline styles drift from the input values for any reason.
    paint();
};

const init = () => {
    document.querySelectorAll('[data-shop-price-range]').forEach(initWrap);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
